'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const wtoTermsSections = [
  {
    number: '1',
    title: 'Scope and Unified Group Applicability',
    content:
      'These General Terms and Conditions shall apply to all services provided under the brand, trade name, or trademark "World Transport Overseas (WTO)" by any of its subsidiaries, affiliated companies, branches, and legally registered entities worldwide (collectively referred to as the "WTO Group Entities").\n\nEach WTO Group Entity operates as a separate legal entity. However, all services performed under the WTO brand shall be governed by a unified legal and operational framework as defined in these General Terms and Conditions together with the FIATA Model Rules for Freight Forwarding Services (2019 Edition).\n\nThese Terms shall apply irrespective of the specific WTO office involved, the country of execution of the service, or the internal allocation of operations between WTO Group Entities.',
  },
  {
    number: '2',
    title: 'Incorporation of FIATA Model Rules (2019 Edition)',
    content:
      'All services provided by any WTO Group Entity are subject to the FIATA Model Rules for Freight Forwarding Services (2019 Edition), which are hereby expressly incorporated into and form an integral part of all quotations, offers, bookings, contracts, transport documents, House Bills of Lading, invoices, electronic communications, and digital services, including the myWTO customer portal.\n\nThe full text of the FIATA Model Rules (2019 Edition) is published on this website and shall be deemed known and accepted by the Customer upon requesting or using any service provided under the WTO brand.',
  },
  {
    number: '3',
    title: 'Contracting Entity',
    content:
      'The contractual party shall be the specific WTO Group Entity issuing the quotation, booking confirmation, invoice, transport document, or House Bill of Lading.\n\nNotwithstanding the above, the Customer expressly agrees that these General Terms and Conditions and the FIATA Model Rules (2019 Edition) shall apply uniformly across all WTO Group Entities involved in the performance of the service, including where multiple WTO offices participate operationally in the same shipment.',
  },
  {
    number: '4',
    title: 'Nature of Services',
    content:
      'Unless expressly agreed otherwise in writing, WTO Group Entities act as freight forwarders and logistics service providers.\n\nDepending on the nature of the service, WTO may also act as a consolidator, Non-Vessel Operating Common Carrier (NVOCC), multimodal transport operator, or contractual carrier, including in cases where a House Bill of Lading is issued.\n\nThe applicable liability regime shall remain subject to the FIATA Model Rules (2019 Edition) and relevant international conventions.',
  },
  {
    number: '5',
    title: 'Right to Subcontract and Use of Third Parties',
    content:
      'WTO Group Entities reserve the unrestricted right to perform services through other WTO Group Entities, agents, partners, carriers, shipping lines, airlines, terminal operators, warehouses, and subcontractors worldwide.\n\nAll services are performed on a back-to-back basis with such third parties. The use of subcontractors shall not increase the liability of any WTO Group Entity beyond the limits established under these Terms, the FIATA Model Rules (2019 Edition), and applicable international conventions.',
  },
  {
    number: '6',
    title: 'House Bill of Lading and Transport Documents',
    content:
      'House Bills of Lading and other transport documents may be issued by different WTO Group Entities depending on the operational structure of the shipment.\n\nAny transport document issued under the WTO brand shall be subject to these General Terms and Conditions and the FIATA Model Rules (2019 Edition).\n\nThe issuing WTO Group Entity shall be the contractual carrier only to the extent expressly stated in the relevant transport document and within the liability limits set forth under applicable conventions and the FIATA Model Rules.',
  },
  {
    number: '7',
    title: 'Limitation of Liability',
    content:
      'To the fullest extent permitted by applicable law, the liability of any WTO Group Entity shall be strictly limited in accordance with the FIATA Model Rules (2019 Edition) and any applicable mandatory international conventions, including but not limited to the CMR Convention, the Montreal Convention, the Hague-Visby Rules, or other applicable legal regimes.\n\nWTO shall not be liable for any indirect, consequential, or economic losses, including but not limited to loss of profit, loss of business, loss of market, contractual penalties, delay damages, or business interruption.\n\nSuch limitations shall apply uniformly across all WTO Group Entities worldwide.',
  },
  {
    number: '8',
    title: 'Consolidation and LCL Services',
    content:
      'In case of groupage, consolidation, or Less-than-Container Load (LCL) services, WTO may act as a consolidator and issue a House Bill of Lading. The Customer acknowledges that the shipment may be consolidated with other cargo and handled through multiple carriers and subcontractors.\n\nThe liability of WTO in such cases shall remain limited in accordance with these Terms, the FIATA Model Rules (2019 Edition), and applicable international conventions.',
  },
  {
    number: '9',
    title: 'High-Value Cargo',
    content:
      'The Customer shall declare in writing the nature and value of any goods of high value prior to shipment.\n\nIn the absence of a written declaration of value and explicit acceptance by WTO, the shipment shall be treated as standard cargo and liability shall remain limited in accordance with the FIATA Model Rules (2019 Edition) and applicable conventions, regardless of the actual value of the goods.\n\nWTO reserves the right to request additional insurance, apply special conditions, or refuse transport of high-value, dangerous, oversized, or project cargo.',
  },
  {
    number: '10',
    title: 'Digital Services and myWTO Portal',
    content:
      'By using the myWTO customer portal, electronic booking systems, tracking tools, automated quotations, EDI connections, or email communications, the Customer agrees that electronic instructions, confirmations, and records shall constitute legally binding contractual evidence.\n\nWTO shall not be liable for delays, disruptions, cyber incidents, system outages, data transmission errors, or failures of third-party digital platforms, unless caused by proven gross negligence.',
  },
  {
    number: '11',
    title: 'Customer Obligations and Accuracy of Information',
    content:
      'The Customer warrants that all information, documentation, cargo descriptions, weight, dimensions, classification, and declarations provided to WTO are complete and accurate.\n\nWTO shall not be liable for any fines, penalties, delays, demurrage, detention, storage costs, customs issues, or additional expenses arising from incorrect, incomplete, or misleading information provided by the Customer.',
  },
  {
    number: '12',
    title: 'Insurance',
    content:
      'Cargo insurance shall only be arranged upon explicit written request and written confirmation by WTO.\n\nIn the absence of such written agreement, WTO shall have no obligation to insure the goods and shall not be liable for uninsured loss or damage.',
  },
  {
    number: '13',
    title: 'Mandatory Law and International Conventions',
    content:
      'Where mandatory local law or international transport conventions apply, such provisions shall prevail only to the extent legally required.\n\nIn all other respects, these General Terms and Conditions and the FIATA Model Rules (2019 Edition) shall remain fully applicable and binding across all jurisdictions.',
  },
  {
    number: '14',
    title: 'Governing Law and Jurisdiction',
    content:
      'Unless otherwise expressly agreed in writing, the governing law and jurisdiction shall be that of the registered seat of the WTO Group Entity issuing the quotation, invoice, booking confirmation, or transport document.\n\nThe unified applicability of these Terms and the FIATA Model Rules (2019 Edition) shall remain binding regardless of the country of execution of the service.',
  },
  {
    number: '15',
    title: 'Acceptance and Binding Effect',
    content:
      'By requesting a quotation, placing a booking, issuing shipping instructions, accepting a transport document, or using any service provided under the WTO brand, the Customer expressly acknowledges and agrees that these General Terms and Conditions together with the FIATA Model Rules for Freight Forwarding Services (2019 Edition) are legally binding without the need for a separate signature.',
  },
];

const fiataPartI = [
  {
    number: '1',
    title: 'Applicability',
    content:
      '1.1. These Rules apply when they are incorporated, however this is made, in writing, orally or otherwise, into a contract by referring to the FIATA Model Rules for Freight Forwarding Services.\n\n1.2. Whenever such reference is made, the parties agree that these Rules shall supersede any additional terms of the contract which are in conflict with these Rules, except insofar as they increase the responsibility or obligations of the Freight Forwarder.',
  },
  {
    number: '2',
    title: 'Definitions',
    content:
      '2.1. Freight Forwarding Services — Means services of any kind relating to the carriage, consolidation, storage, handling, packing or distribution of the Goods as well as ancillary and advisory services in connection therewith, including but not limited to customs and fiscal matters, declaring the Goods for official purposes, procuring insurance of the Goods and collecting or procuring payment or documents relating to the Goods.\n\n2.2. Freight Forwarder — Means the person concluding a contract of Freight Forwarding Services with a Customer.\n\n2.3. Carrier — Means any person actually performing the carriage of the Goods with his own means of transport (performing Carrier) and any person subject to carrier liability as a result of an express or implied undertaking to assume such liability (contracting Carrier).\n\n2.4. Customer — Means any person having rights or obligations under the contract of Freight Forwarding Services concluded with a Freight Forwarder or as a result of his activity in connection with such services.\n\n2.5. Goods — Means any property including live animals as well as containers, pallets or similar articles of transport or packaging not supplied by the Freight Forwarder.\n\n2.6. SDR — Means a Special Drawing Right as defined by the International Monetary Fund.\n\n2.7. Mandatory Law — Means any statutory law the provisions of which cannot be departed from by contractual stipulations to the detriment of the Customer.\n\n2.8. In Writing — Includes written information generated, sent, received or stored by electronic, magnetic, optical or similar means, including, but not limited to, electronic data interchange, telegram, telex, telefax or electronic mail, if the information is accessible so as to be usable for subsequent reference.\n\n2.9. Valuables — Means bullion, coins, money, negotiable instruments, precious stones, jewellery, antiques, pictures, works of art and similar properties whose value clearly exceeds the regular value of ordinary commercial goods or merchandise.\n\n2.10. Dangerous Goods — Means Goods which are officially classified as hazardous as well as Goods which are or may become of a dangerous, inflammable, radioactive, noxious or damaging nature.',
  },
  {
    number: '3',
    title: 'Insurance',
    content:
      'No insurance will be effected by the Freight Forwarder, except upon express instructions given in writing by the Customer. All insurances effected are subject to the usual exceptions and conditions of the Policies of the Insurance Company or Underwriters taking the risk.\n\nUnless otherwise agreed in writing, the Freight Forwarder shall not be under any obligation to effect a separate insurance on each consignment, but may declare it on any open or general Policy held by the Freight Forwarder.',
  },
  {
    number: '4',
    title: 'Hindrances',
    content:
      "If at any time the Freight Forwarder's performance is or is likely to be affected by any hindrance or risk of any kind (including the conditions of the Goods) not arising from any fault or neglect of the Freight Forwarder and which cannot be avoided by the exercise of reasonable endeavour, the Freight Forwarder may abandon the carriage of the Goods under the respective contract and, where reasonably possible, make the Goods or any part of them available to the Customer at a place which the Freight Forwarder may deem safe and convenient.\n\nWhereupon delivery shall be deemed to have been made, and the responsibility of the Freight Forwarder in respect of such Goods shall cease.\n\nIn any event, the Freight Forwarder shall be entitled to the agreed remuneration under the contract and the Customer shall pay any additional costs resulting from the above-mentioned circumstances.",
  },
  {
    number: '5',
    title: 'Method and Route of Transportation',
    content:
      "The Freight Forwarder shall carry out his services according to the Customer's instructions as agreed. If the instructions are inaccurate, incomplete or not according to contract, the Freight Forwarder may at the risk and expense of the Customer act as he deems fit.\n\nUnless otherwise agreed, the Freight Forwarder may without notice to the Customer arrange to carry the Goods on or under deck.\n\nUnless otherwise agreed, the Freight Forwarder may, upon reasonable efforts to inform the Customer, choose or substitute the means, route and procedure to be followed in the handling, stowage, storage and transportation of the Goods.",
  },
];

const fiataPartII = [
  {
    number: '6',
    title: "The Freight Forwarder's Liability (Except as Principal)",
    content:
      "6.1. Basis of Liability\n\n6.1.1. The Freight Forwarder's Duty of Care — The Freight Forwarder is liable if he fails to exercise due diligence and take reasonable measures in the performance of the Freight Forwarding Services, in which case he, subject to Art. 8, shall compensate the Customer for: Loss of or damage to the Goods; and Direct financial loss resulting from breach of his duty of care.\n\n6.1.2. No Liability for Third Parties — The Freight Forwarder is not liable for acts and omissions by third parties, such as, but not limited to: Carriers, Warehousemen, Stevedores, Port authorities, Other freight forwarders. Unless he has failed to exercise due diligence in selecting, instructing or supervising such third parties.",
  },
  {
    number: '7',
    title: "The Freight Forwarder\'s Liability as Principal",
    content:
      "7.1. Liability as Carrier — The Freight Forwarder is subject to liability as principal: When he actually performs the carriage himself by his own means of transport (performing Carrier); and If, by issuing his own transport document or otherwise, he has made an express or implied undertaking to assume Carrier liability (contracting Carrier).\n\nHowever, the Freight Forwarder shall not be deemed liable as Carrier if the Customer has received a transport document issued by a person other than the Freight Forwarder and does not within a reasonable time maintain that the Freight Forwarder is nevertheless liable as Carrier.\n\n7.2. Liability as Principal for Other Services — With respect to services other than carriage of Goods (such as storage, handling, packing or distribution, as well as ancillary services), the Freight Forwarder shall be liable as principal: When such services have been performed by himself using his own facilities or employees; or If he has made an express or implied undertaking to assume liability as principal.\n\n7.3. Basis of Liability as Principal — The Freight Forwarder as principal shall, subject to Art. 8, be responsible for the acts and omissions of third parties he has engaged for performance of the contract in the same manner as if such acts and omissions were his own. His rights and duties shall be subject to: The law applicable to the mode of transport or service concerned; and Any additional conditions expressly agreed, or failing such agreement, the usual conditions for such mode of transport or services.",
  },
  {
    number: '8',
    title: 'Exclusions, Assessment and Monetary Limits of Liability',
    content:
      '8.1. Exclusions — The Freight Forwarder shall in no event be liable for: Valuables or Dangerous Goods unless declared at the time of contract; Loss following from delay unless expressly agreed in writing; Indirect or consequential loss (including loss of profit or market); Loss or damage due to inherent defect of the Goods; Acts or omissions of the Customer or its agents; Improper packing or marking, unless liable as principal under Art. 7.2.\n\n8.2. Assessment of Compensation — The value of the Goods shall be determined: According to the current commodity exchange price; If none, according to the current market price; If none, by reference to the normal value of Goods of the same kind and quality.\n\n8.3. Monetary Limits\n\n8.3.1. Loss of or Damage to the Goods — Liability shall not exceed 2 SDR per kilogram of gross weight of the Goods lost or damaged unless a larger amount is recovered from a responsible party. If Goods are not delivered within 90 consecutive days after the date when they ought to have been delivered, they may be treated as lost in the absence of evidence to the contrary.\n\n8.3.2. Delay — Liability for delay shall be limited to the remuneration relating to the service giving rise to the delay.\n\n8.3.3. Other Types of Loss — Liability shall not exceed the applicable SDR limit per incident unless a larger amount is recovered from a responsible party.',
  },
  {
    number: '9',
    title: 'Notice',
    content:
      '9.1. Notice of apparent loss or damage must be given in writing at the time of delivery. If not apparent, notice must be given within 6 consecutive days after delivery. Failing this, delivery is prima facie evidence that the Goods were delivered in good order and condition.\n\n9.2. All other claims must be made in writing within 14 days from when the Customer became or should have become aware of the event giving rise to the claim. Failure to comply results in waiver of the claim unless compliance was impossible.',
  },
  {
    number: '10',
    title: 'Time Bar',
    content:
      'Unless otherwise agreed, the Freight Forwarder is discharged from liability unless suit is brought within 9 months after: Delivery of the Goods; The date when Goods should have been delivered; or The date when non-delivery gives the right to treat the Goods as lost.\n\nFor other types of loss, the 9-month period runs from the date of the failure giving rise to the claim.',
  },
  {
    number: '11',
    title: 'Applicability to Actions in Tort',
    content: 'These Rules apply to all claims whether founded in contract or in tort.',
  },
  {
    number: '12',
    title: 'Liability of Servants and Other Persons',
    content:
      'These Rules apply to claims against servants, agents or independent contractors engaged by the Freight Forwarder.\n\nThe aggregate liability shall not exceed the applicable limit under these Rules.',
  },
];

const fiataPartIII = [
  {
    number: '13',
    title: 'Unforeseen Circumstances',
    content:
      'If the Freight Forwarder acts in the best interest of the Customer in unforeseen circumstances, extra costs and charges shall be borne by the Customer.',
  },
  {
    number: '14',
    title: 'No Set-Off',
    content:
      'All monies due shall be paid without reduction or deferment on account of any claim, counterclaim or set-off.',
  },
  {
    number: '15',
    title: 'General Lien',
    content:
      'The Freight Forwarder shall have a general lien on the Goods and related documents for any amount due, including storage fees and recovery costs, and may enforce such lien in any reasonable manner.',
  },
  {
    number: '16',
    title: 'Information',
    content:
      'The Customer guarantees the accuracy of all particulars relating to: Nature of the Goods, Marks, Number, Weight, Volume, Quantity, Dangerous character (if applicable).',
  },
  {
    number: '17',
    title: 'Duty of Indemnification',
    content:
      '17.1. General — Except where the Freight Forwarder is liable under Part II, the Customer shall indemnify the Freight Forwarder for all liability incurred.\n\n17.2. General Average — The Customer shall indemnify the Freight Forwarder for any General Average claims and provide required security.',
  },
  {
    number: '18',
    title: "The Customer\'s Liability",
    content:
      'The Customer shall be liable for all loss, damage, costs, expenses and charges resulting from: Inaccurate or incomplete information or instructions; Goods causing death, injury, property damage, environmental damage or other loss.',
  },
];

const fiataPartIV = [
  {
    number: '19',
    title: 'Jurisdiction and Applicable Law',
    content:
      'Unless otherwise agreed, actions against the Freight Forwarder may be instituted only where the Freight Forwarder has his principal place of business.\n\nThe applicable law shall be the law of that country.',
  },
  {
    number: '20',
    title: 'Mandatory Law',
    content:
      'These Rules take effect only to the extent they are not contrary to mandatory provisions of international conventions or national law applicable to the Freight Forwarding Services.',
  },
];

interface AccordionItemProps {
  number: string;
  title: string;
  content: string;
  accentColor?: string;
}

function AccordionItem({ number, title, content, accentColor = '#1B6B8A' }: AccordionItemProps) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b last:border-0 transition-colors"
      style={{ borderColor: 'rgba(15,35,64,0.07)' }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 py-4 text-left group focus:outline-none"
      >
        <span
          className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
          style={{ background: accentColor }}
        >
          {number}
        </span>
        <span className="flex-1 font-semibold text-gray-800 group-hover:text-[#1B6B8A] transition-colors text-sm md:text-base leading-snug">
          {title}
        </span>
        <span
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300"
          style={{
            background: open ? accentColor : 'rgba(15,35,64,0.06)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke={open ? 'white' : '#6B7280'} strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="pb-5 pl-11 pr-2">
          {content.split('\n\n').map((para, i) => (
            <p key={i} className="text-gray-600 text-sm leading-relaxed mb-3 last:mb-0">
              {para}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

interface SectionCardProps {
  id: string;
  label: string;
  partColor: string;
  partTag?: string;
  partLabel?: string;
  items: AccordionItemProps[];
  accentColor: string;
}

function SectionCard({ id, label, partColor, partTag, partLabel, items, accentColor }: SectionCardProps) {
  return (
    <div id={id} className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        {partTag && (
          <span
            className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-lg"
            style={{ background: partColor, color: 'white' }}
          >
            {partTag}
          </span>
        )}
        <h2 className="text-lg md:text-xl font-bold" style={{ color: '#0F2340' }}>
          {label}
        </h2>
      </div>
      {partLabel && (
        <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: partColor }}>
          {partLabel}
        </p>
      )}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'white',
          border: '1px solid rgba(15,35,64,0.08)',
          boxShadow: '0 2px 16px rgba(15,35,64,0.06)',
        }}
      >
        <div
          className="h-1 w-full"
          style={{ background: `linear-gradient(90deg, ${partColor}, ${accentColor})` }}
        />
        <div className="px-6 py-1">
          {items.map((item) => (
            <AccordionItem
              key={item.number}
              number={item.number}
              title={item.title}
              content={item.content}
              accentColor={accentColor}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const tocItems = [
  { id: 'wto-terms', label: 'WTO General Terms', count: '15 clauses' },
  { id: 'applicability', label: 'Applicability Notice', count: '' },
  { id: 'fiata-i', label: 'FIATA Part I — General', count: '5 articles' },
  { id: 'fiata-ii', label: 'FIATA Part II — Liability', count: '7 articles' },
  { id: 'fiata-iii', label: 'FIATA Part III — Customer', count: '6 articles' },
  { id: 'fiata-iv', label: 'FIATA Part IV — Disputes', count: '2 articles' },
  { id: 'hbl', label: 'HBL Terms', count: 'PDF' },
];

export default function GeneralTermsPage() {
  const [activeSection, setActiveSection] = useState('wto-terms');

  useEffect(() => {
    const handleScroll = () => {
      const ids = tocItems.map((t) => t.id);
      for (let i = ids.length - 1; i >= 0; i--) {
        const el = document.getElementById(ids[i]);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(ids[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F7F9FC' }}>
      <Header />

      {/* Hero */}
      <div
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0F2340 0%, #1B6B8A 60%, #1A7FA0 100%)' }}
      >
        {/* Decorative grid */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        {/* Decorative blobs */}
        <div
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #7EC8DC, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-10 -left-10 w-64 h-64 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #1B6B8A, transparent 70%)' }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
                style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.15)' }}
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 02-2 2z" />
                </svg>
                Legal Documentation
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                General Terms<br />
                <span style={{ color: '#7EC8DC' }}>& Conditions</span>
              </h1>
              <p className="text-base md:text-lg max-w-xl" style={{ color: 'rgba(255,255,255,0.65)' }}>
                WTO Group General Terms and Conditions governing all services provided under the World Transport Overseas brand worldwide.
              </p>
            </div>

            {/* Quick stats */}
            <div className="flex gap-6 md:gap-8 flex-shrink-0">
              {[
                { value: '15', label: 'WTO Clauses' },
                { value: '20', label: 'FIATA Articles' },
                { value: '2019', label: 'Edition' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold" style={{ color: '#7EC8DC' }}>{stat.value}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main layout */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="flex gap-10 items-start">

          {/* Sticky TOC sidebar */}
          <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-24">
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                background: 'white',
                border: '1px solid rgba(15,35,64,0.08)',
                boxShadow: '0 2px 16px rgba(15,35,64,0.06)',
              }}
            >
              <div
                className="px-4 py-3 text-xs font-bold uppercase tracking-widest"
                style={{ background: '#0F2340', color: 'rgba(255,255,255,0.7)' }}
              >
                Contents
              </div>
              <nav className="py-2">
                {tocItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className="w-full text-left px-4 py-2.5 flex items-start gap-2 transition-all group"
                    style={{
                      background: activeSection === item.id ? 'rgba(27,107,138,0.07)' : 'transparent',
                      borderLeft: activeSection === item.id ? '3px solid #1B6B8A' : '3px solid transparent',
                    }}
                  >
                    <span
                      className="text-xs leading-snug font-medium transition-colors"
                      style={{ color: activeSection === item.id ? '#1B6B8A' : '#4B5563' }}
                    >
                      {item.label}
                    </span>
                    {item.count && (
                      <span
                        className="ml-auto flex-shrink-0 text-xs px-1.5 py-0.5 rounded-md font-medium"
                        style={{
                          background: activeSection === item.id ? 'rgba(27,107,138,0.12)' : 'rgba(15,35,64,0.05)',
                          color: activeSection === item.id ? '#1B6B8A' : '#9CA3AF',
                        }}
                      >
                        {item.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Back link */}
            <Link
              href="/home-page"
              className="mt-4 flex items-center gap-2 text-xs font-medium px-4 py-2.5 rounded-xl transition-colors"
              style={{ color: '#1B6B8A', background: 'rgba(27,107,138,0.07)' }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-10">

            {/* WTO Terms */}
            <SectionCard
              id="wto-terms"
              label="WTO Group General Terms and Conditions"
              partColor="#0F2340"
              accentColor="#1B6B8A"
              items={wtoTermsSections}
            />

            {/* Applicability Notice */}
            <div id="applicability" className="scroll-mt-24">
              <div
                className="rounded-2xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #EBF5FB 0%, #D6EAF8 100%)',
                  border: '1px solid rgba(27,107,138,0.15)',
                  boxShadow: '0 2px 16px rgba(15,35,64,0.06)',
                }}
              >
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #1B6B8A, #7EC8DC)' }} />
                <div className="p-6 md:p-8 flex items-start gap-5">
                  <div
                    className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ background: '#1B6B8A' }}
                  >
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base font-bold mb-3" style={{ color: '#0F2340' }}>Applicability Notice</h2>
                    <p className="text-gray-700 text-sm leading-relaxed mb-2">
                      All services provided by World Transport Overseas (WTO) and its affiliated group entities worldwide are subject to the{' '}
                      <strong>FIATA Model Rules for Freight Forwarding Services (2019 Edition)</strong>.
                    </p>
                    <p className="text-gray-700 text-sm leading-relaxed mb-2">
                      These Rules are hereby expressly incorporated into and form an integral part of all quotations, bookings, contracts, transport documents, House Bills of Lading, invoices, and services provided under the WTO brand.
                    </p>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      The official text of the FIATA Model Rules (2019 Edition) is published below without modification.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FIATA heading */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="h-px flex-1" style={{ background: 'rgba(15,35,64,0.1)' }} />
                <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full" style={{ background: '#0F2340', color: 'white' }}>
                  FIATA Model Rules
                </span>
                <div className="h-px flex-1" style={{ background: 'rgba(15,35,64,0.1)' }} />
              </div>
              <p className="text-center text-xs text-gray-500 mt-1">Freight Forwarding Services — 2019 Edition</p>
            </div>

            {/* FIATA Part I */}
            <SectionCard
              id="fiata-i"
              label="General Provisions"
              partColor="#0F2340"
              partTag="Part I"
              accentColor="#0F2340"
              items={fiataPartI}
            />

            {/* FIATA Part II */}
            <SectionCard
              id="fiata-ii"
              label="The Freight Forwarder's Liability"
              partColor="#1A5276"
              partTag="Part II"
              accentColor="#1A5276"
              items={fiataPartII}
            />

            {/* FIATA Part III */}
            <SectionCard
              id="fiata-iii"
              label="The Customer's Obligations and Liability"
              partColor="#1B6B8A"
              partTag="Part III"
              accentColor="#1B6B8A"
              items={fiataPartIII}
            />

            {/* FIATA Part IV */}
            <SectionCard
              id="fiata-iv"
              label="Disputes and Mandatory Law"
              partColor="#117A65"
              partTag="Part IV"
              accentColor="#117A65"
              items={fiataPartIV}
            />

            {/* HBL Terms */}
            <div id="hbl" className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-lg md:text-xl font-bold" style={{ color: '#0F2340' }}>
                  House Bill of Lading (HBL) Terms
                </h2>
              </div>
              <div
                className="rounded-2xl overflow-hidden"
                style={{
                  background: 'white',
                  border: '1px solid rgba(15,35,64,0.08)',
                  boxShadow: '0 2px 16px rgba(15,35,64,0.06)',
                }}
              >
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #1B6B8A, #117A65)' }} />
                <div className="p-6 md:p-8">
                  <p className="text-gray-600 text-sm leading-relaxed mb-2">
                    All House Bills of Lading issued under the World Transport Overseas (WTO) brand are subject to the WTO House Bill of Lading Terms and Conditions published herein.
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    These Terms apply together with the WTO Group General Terms and Conditions and the FIATA Model Rules for Freight Forwarding Services (2019 Edition) and are incorporated into all HBL, bookings, and transport services provided by WTO.
                  </p>
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{ height: '500px', border: '1px solid rgba(15,35,64,0.1)' }}
                  >
                    <iframe
                      src="https://drive.google.com/file/d/1VA42VDmKiLDJLmV4i7bVlY3-1TEhpdWv/preview"
                      className="w-full h-full"
                      title="Bill of Lading WTO Group Terms"
                      allow="autoplay"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile back link */}
            <div className="lg:hidden text-center pt-2">
              <Link
                href="/home-page"
                className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
                style={{ color: '#1B6B8A' }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
